import React, { useState, useEffect } from 'react';
import ktbackground from '../ktbackground.webp';
import ktcardbackground from '../ktcardbackground.webp';
import cardbackground from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import { supabase } from '../supabaseClient';
import AstrologyFields from './AstrologyFields'; // Import the new AstrologyFields component
import HumanDesignFields from './HumanDesignFields';

function KnowThyself({ goBack, goHome }) {
    const [worksheetData, setWorksheetData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionProgress, setSessionProgress] = useState({
        session_count: 0,
        last_session_date: 'None',
    });

    useEffect(() => {
        const fetchWorksheetData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const { data: { user }, error: userError } = await supabase.auth.getUser();
                console.log('User fetch result:', { user, userError }); // Debug log
                if (userError || !user) {
                    console.error('Error fetching user:', userError?.message);
                    setError('Please log in to view your data.');
                    setIsLoading(false);
                    return;
                }

                let data = null;
                let error = null;
                try {
                    const response = await supabase
                        .schema('api')
                        .from('worksheets')
                        .select('weaknesses, strengths, values, accomplishments, creativity, dream, gratitude, astrology, sun, moon, ascendant, venus, mercury, mars, north_node, mideheaven, energy_type, strategy, inner_authority, profile, definition, incarnation_cross')
                        .eq('id', user.id);
                    if (response.error) throw response.error;
                    if (Array.isArray(response.data) && response.data.length === 1) {
                        data = response.data[0];
                    } else {
                        data = {};
                    }
                } catch (fetchErr) {
                    error = fetchErr;
                }
                console.log('Worksheet fetch result:', { data, error }); // Debug log
                // Do not show error if no data, just show empty fields
                setWorksheetData(data || {});
                setIsLoading(false);
            } catch (err) {
                console.error('Unexpected error fetching worksheet data:', err);
                setError('An unexpected error occurred while loading data.');
                setIsLoading(false);
            }
        };

        const fetchSessionProgress = async () => {
            try {
                const { data: { user }, error: userError } = await supabase.auth.getUser();
                if (userError || !user) {
                    console.error('Error fetching user:', userError);
                    return;
                }

                const { data: profile, error: profileError } = await supabase
                    .schema('api')
                    .from('user_profiles')
                    .select('session_count, last_session_date')
                    .eq('id', user.id)
                    .single();

                if (profileError) {
                    console.error('Error fetching session progress:', profileError.message);
                    return;
                }

                setSessionProgress({
                    session_count: profile.session_count || 0,
                    last_session_date: profile.last_session_date
                        ? new Date(profile.last_session_date).toLocaleDateString()
                        : 'None',
                });
            } catch (err) {
                console.error('Unexpected error fetching session progress:', err);
            }
        };

        fetchWorksheetData();
        fetchSessionProgress();
    }, []);

    const handleSaveProgressInternal = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                console.error('Error fetching user:', userError);
                alert('User not logged in!');
                return;
            }

            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert({
                    user_id: user.id,
                    current_question_id: null,
                    current_component: 'knowThyself',
                    animated_helper: 'no', // <-- ensure this is always set to 'no'
                    timestamp: new Date().toISOString(),
                });

            if (progressError) {
                console.error('Error saving progress:', progressError.message);
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            alert('Progress saved successfully!');
        } catch (err) {
            console.error('Unexpected error saving progress:', err);
            alert('An unexpected error occurred while saving progress.');
        }
    };

    if (isLoading) {
        return (
            <div
                style={{
                    backgroundImage: `url(${ktbackground})`,
                    backgroundSize: 'cover',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                }}
            >
                <p
                    style={{
                        color: '#4B5563',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '20px',
                        fontWeight: 'bold',
                    }}
                >
                    Loading...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    backgroundImage: `url(${ktbackground})`,
                    backgroundSize: 'cover',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                }}
            >
                <p
                    style={{
                        color: '#DC2626',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '20px',
                        fontWeight: 'bold',
                    }}
                >
                    {error}
                </p>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginTop: '16px',
                    }}
                    onClick={() => goHome?.()}
                >
                    Home
                </button>
            </div>
        );
    }

    return (
        <div
            style={{
                backgroundImage: `url(${ktbackground})`,
                backgroundSize: 'cover',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
                paddingBottom: '100px', // Add padding to prevent sticky footer overlap
                marginBottom: undefined, // Remove marginBottom if present
                boxSizing: 'border-box',
            }}
        >
            <style>
                {`
                @media (max-width: 600px) {
                    .card-row {
                        flex-direction: column !important;
                        gap: 16px !important;
                    }
                    .small-card {
                        width: 100% !important;
                        max-width: 600px !important;
                        min-height: 100px !important;
                    }
                }
                `}
            </style>
            <h2
                style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: '#4B5563',
                    margin: '32px 0 24px 0',
                    textAlign: 'center',
                }}
            >
                Know Thyself
            </h2>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    width: '100%',
                    maxWidth: '700px',
                    alignItems: 'center',
                }}
            >
                {/* Card 1 */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        minHeight: '120px',
                        background: '#FFFFFF',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                    }}
                >
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: 0,
                        }}
                    >
                        Self-knowledge is the foundation of personal growth and fulfillment. Reflect on your values, strengths, and areas for improvement.
                    </p>
                </div>
                {/* Row 1: Strengths, Weaknesses, Values */}
                <div
                    className="card-row"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '800px',
                        justifyContent: 'center',
                    }}
                >
                    {/* Strengths Card */}
                    <div
                        className="small-card"
                        style={{
                            width: '33%',
                            minHeight: '100px',
                            backgroundImage: `url(${ktcardbackground})`,
                            backgroundSize: 'cover',
                            borderRadius: '10px',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            transform: `rotate(${Math.random() * 4 - 2}deg)`,
                        }}
                    >
                        <h3
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '0 0 8px 0',
                            }}
                        >
                            Strengths
                        </h3>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                margin: 0,
                            }}
                        >
                            {worksheetData.strengths || 'No strengths added yet.'}
                        </p>
                    </div>
                    {/* Weaknesses Card */}
                    <div
                        className="small-card"
                        style={{
                            width: '33%',
                            minHeight: '100px',
                            backgroundImage: `url(${ktcardbackground})`,
                            backgroundSize: 'cover',
                            borderRadius: '10px',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            transform: `rotate(${Math.random() * 4 - 2}deg)`,
                        }}
                    >
                        <h3
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '0 0 8px 0',
                            }}
                        >
                            Weaknesses
                        </h3>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                margin: 0,
                            }}
                        >
                            {worksheetData.weaknesses || 'No weaknesses added yet.'}
                        </p>
                    </div>
                    {/* Values Card */}
                    <div
                        className="small-card"
                        style={{
                            width: '33%',
                            minHeight: '100px',
                            backgroundImage: `url(${ktcardbackground})`,
                            backgroundSize: 'cover',
                            borderRadius: '10px',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            transform: `rotate(${Math.random() * 4 - 2}deg)`,
                        }}
                    >
                        <h3
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '0 0 8px 0',
                            }}
                        >
                            Values
                        </h3>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                margin: 0,
                            }}
                        >
                            {worksheetData.values || 'No values added yet.'}
                        </p>
                    </div>
                </div>
                {/* Human Design Card */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        minHeight: '120px',
                        background: '#FFFFFF',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        padding: '8px 4px 8px 4px', // Reduce all padding, especially left
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '0 auto',
                    }}
                >
                    <h3
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            margin: '0 0 8px 0',
                            width: '100%',
                            textAlign: 'center',
                        }}
                    >
                        Human Design
                    </h3>
                    <HumanDesignFields worksheetData={worksheetData} />
                    <h3
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            margin: '16px 0 8px 0',
                            width: '100%',
                            textAlign: 'center',
                        }}
                    >
                        Astrology
                    </h3>
                    {/* Top portion: 8 fill-in fields */}
                    <AstrologyFields worksheetData={worksheetData} userId={worksheetData.id} />
                    {/* Bottom portion: worksheet astrology column */}
                    <div style={{ marginTop: '16px', width: '100%' }}>
                        <div style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            margin: 0,
                            whiteSpace: 'pre-line',
                        }}>
                            {worksheetData.astrology || 'No astrology worksheet data added yet.'}
                        </div>
                    </div>
                </div>
                {/* Row 2: Accomplishments, Gratitude, Dream */}
                <div
                    className="card-row"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '800px',
                        justifyContent: 'center',
                    }}
                >
                    {/* Accomplishments Card */}
                    <div
                        className="small-card"
                        style={{
                            width: '33%',
                            minHeight: '100px',
                            backgroundImage: `url(${ktcardbackground})`,
                            backgroundSize: 'cover',
                            borderRadius: '10px',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            transform: `rotate(${Math.random() * 4 - 2}deg)`,
                        }}
                    >
                        <h3
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '0 0 8px 0',
                            }}
                        >
                            Accomplishments
                        </h3>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                margin: 0,
                            }}
                        >
                            {worksheetData.accomplishments || 'No accomplishments added yet.'}
                        </p>
                    </div>
                    {/* Gratitude Card */}
                    <div
                        className="small-card"
                        style={{
                            width: '33%',
                            minHeight: '100px',
                            backgroundImage: `url(${ktcardbackground})`,
                            backgroundSize: 'cover',
                            borderRadius: '10px',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            transform: `rotate(${Math.random() * 4 - 2}deg)`,
                        }}
                    >
                        <h3
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '0 0 8px 0',
                            }}
                        >
                            Gratitude
                        </h3>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                margin: 0,
                            }}
                        >
                            {worksheetData.gratitude || 'No gratitude entries added yet.'}
                        </p>
                    </div>
                    {/* Dream Card */}
                    <div
                        className="small-card"
                        style={{
                            width: '33%',
                            minHeight: '100px',
                            backgroundImage: `url(${ktcardbackground})`,
                            backgroundSize: 'cover',
                            borderRadius: '10px',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            transform: `rotate(${Math.random() * 4 - 2}deg)`,
                        }}
                    >
                        <h3
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                margin: '0 0 8px 0',
                            }}
                        >
                            Dream
                        </h3>
                        <p
                            style={{
                                color: '#4B5563',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                margin: 0,
                            }}
                        >
                            {worksheetData.dream || 'No dreams added yet.'}
                        </p>
                    </div>
                </div>
                {/* Creativity Card */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        minHeight: '120px',
                        background: '#FFFFFF',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '0 auto',
                    }}
                >
                    <h3
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            margin: '0 0 8px 0',
                            width: '100%',
                            textAlign: 'center',
                        }}
                    >
                        Creativity
                    </h3>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: 0,
                        }}
                    >
                        Creativity is the power to imagine, invent, and express yourself in new ways. Explore your creative side and see what you can bring into the world. <br />Taking one thing and from it creating another is sort of what we are doing when we change our habits and self heal. It's the transformative art of becoming whole. <br />We are referring to spiritual alchemy of course.  Integrating the shadow to individuate into wholeness.  In shadow we can unconsciously indulge our lower natures and perpetuate limiting, unwanted patterns.  We can also find our disallowed favorable traits in our shadow as well.  Bringing this to light and integrating those parts of ourselves can often reveal golden experiences, wisdoms, and feelings. <br /> Alchemy is full of symbols that have been passed down, but the main thing about it for me is the metaphor that it carries for bringing ourselves to a higher plane of existence through claiming and loving those unloved parts of ourselves.

You cannot alchemize aspects of self without getting to know yourself.  The process is the most powerful part, not the process itself but the being in process with self.
                    </p>
                </div>
                {/* Last Card */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        minHeight: '120px',
                        background: '#FFFFFF',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                    }}
                >
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: 0,
                        }}
                    >
                        Self-knowledge is the foundation of personal growth and fulfillment. Reflect on your values, strengths, and areas for improvement.
                        Understanding your motivations and patterns helps you make conscious choices and build healthier relationships. Use this space to journal, reflect, and explore who you are becoming.
                    </p>
                </div>
                {/* Add the new card at the bottom */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        minHeight: '120px',
                        background: '#FFFFFF',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '0 auto',
                    }}
                >
                    <h3
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            marginBottom: '16px',
                            textAlign: 'center',
                        }}
                    >
                        Session Progress
                    </h3>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            marginBottom: '8px',
                        }}
                    >
                        Completed Sessions: {sessionProgress.session_count}
                    </p>
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }}
                    >
                        Date of Last Session: {sessionProgress.last_session_date}
                    </p>
                </div>
            </div>
            {/* Sticky Footer with duplicated buttons */}
            <footer
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    backgroundImage: `url(${cardbackground})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px 10px 0 0',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    padding: '10px',
                    textAlign: 'center',
                    zIndex: 1000,
                    minHeight: '60px',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '10px',
                }}
            >
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={() => goBack?.()}
                >
                    Back
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={() => goHome?.()}
                >
                    Home
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleSaveProgressInternal}
                >
                    Save
                </button>
            </footer>
        </div>
    );
}

export default KnowThyself;